import {
  contentDisplayTimeZone,
  contentStatuses,
  type ContentStatus,
  type EffectiveVisibility,
} from '@apuestapr/contracts';
import { DateTimeInput, Field, Select } from '@apuestapr/ui';
import { type ReactElement } from 'react';

import { VisibilityBadge } from './VisibilityBadge.tsx';

const statusLabels: Record<ContentStatus, string> = {
  draft: 'Borrador',
  scheduled: 'Programado',
  published: 'Publicado',
  archived: 'Archivado',
};

export interface PublishValues {
  readonly status: ContentStatus;
  readonly publishAt: string | null;
  readonly unpublishAt: string | null;
}

export interface PublishFieldsProps {
  readonly values: PublishValues;
  readonly effectiveVisibility: EffectiveVisibility | null;
  readonly errors: Readonly<Record<string, string | undefined>>;
  readonly disabled?: boolean;
  readonly onChange: <Key extends keyof PublishValues>(key: Key, value: PublishValues[Key]) => void;
}

export const publishFieldId = (path: 'status' | 'publishAt' | 'unpublishAt'): string =>
  `content-field-${path}`;

export const PublishFields = ({
  values,
  effectiveVisibility,
  errors,
  disabled = false,
  onChange,
}: PublishFieldsProps): ReactElement => (
  <fieldset className="grid gap-5 rounded-xl border border-border bg-card p-5">
    <legend className="px-2 font-display text-lg">Publicación</legend>
    <Field controlId={publishFieldId('status')} error={errors.status} label="Estado" required>
      {(control) => (
        <Select
          {...control}
          disabled={disabled}
          value={values.status}
          onChange={(event) => {
            const status = event.currentTarget.value as ContentStatus;
            onChange('status', status);
            if (status === 'scheduled' && values.publishAt === null) {
              queueMicrotask(() => document.getElementById(publishFieldId('publishAt'))?.focus());
            }
          }}
        >
          {contentStatuses.map((status) => (
            <option key={status} value={status}>
              {statusLabels[status]}
            </option>
          ))}
        </Select>
      )}
    </Field>
    <Field
      controlId={publishFieldId('publishAt')}
      description="Hora de Puerto Rico; se guarda como un instante UTC."
      error={errors.publishAt}
      label="Fecha y hora de publicación"
      required={values.status === 'scheduled'}
    >
      {(control) => (
        <DateTimeInput
          {...control}
          disabled={disabled}
          name="publishAt"
          value={values.publishAt}
          zone={contentDisplayTimeZone}
          onChange={(value) => onChange('publishAt', value)}
        />
      )}
    </Field>
    <Field
      controlId={publishFieldId('unpublishAt')}
      description="Opcional. Borrar fecha y hora envía null."
      error={errors.unpublishAt}
      label="Fecha y hora de despublicación"
    >
      {(control) => (
        <DateTimeInput
          {...control}
          disabled={disabled}
          name="unpublishAt"
          value={values.unpublishAt}
          zone={contentDisplayTimeZone}
          onChange={(value) => onChange('unpublishAt', value)}
        />
      )}
    </Field>
    <div className="grid gap-2">
      <h3 className="m-0 text-base">Visibilidad efectiva</h3>
      {effectiveVisibility === null ? (
        <p className="m-0 text-muted-foreground">Se calculará en el servidor después de guardar.</p>
      ) : (
        <VisibilityBadge visibility={effectiveVisibility} />
      )}
      <p className="m-0 text-sm text-muted-foreground">
        El servidor combina el estado y la ventana de publicación para calcular esta visibilidad.
      </p>
      {values.status === 'scheduled' ? (
        <p className="m-0 text-sm">Programar requiere una fecha y hora de publicación.</p>
      ) : null}
      {values.status === 'archived' ? (
        <p className="m-0 text-sm">
          Archivar retira el contenido sin eliminarlo; la entrada y su historial se conservan.
        </p>
      ) : null}
    </div>
  </fieldset>
);
